<template>
  <div class="epost">
    <v-form v-model="formValid" class="epost__form" ref="epost-form" @submit.prevent="processForm">
      <label class="epost__field-title"><sc-text :field="fields.RequestTypeLabel" /></label>
      <v-select
        class="epost__request-type"
        outlined
        v-model="tempFormData.requestType"
        item-value="value"
        :label="fields.RequestTypePlaceholder.value"
        :items="requestTypeItems"
        :rules="validation.requestTypeRules"
        background-color="white"
        solo
      />

      <label class="epost__field-title"
        ><sc-text :field="fields.SupportingInformationLabel"
      /></label>
      <v-textarea
        class="epost__supporting-information"
        outlined
        :height="$vuetify.breakpoint.smAndDown ? 134 : 152"
        v-model="formData.supportingInformation"
        :placeholder="fields.SupportingInformationPlaceholder.value"
        counter
        no-resize
        :maxlength="fields.SupportingInformationCharacterLimit.value"
        background-color="white"
      />
      <h2 class="epost__subtitle">
        <sc-text :field="fields.FileSelectionSubtitle" />
      </h2>
      <div class="epost__documents-description">
        <sc-rich-text :field="fields.FileSelectionDescription" />
      </div>
      <v-btn
        class="epost__choose-files-button"
        v-html="fields.FileSelectionButtonLabel.value"
        @click="openFileSelection"
        :disabled="formData.files.length >= 5 || loading"
      />
      <label class="epost__file-selector-label"
        ><sc-text :field="fields.FileSelectionLabel"
      /></label>
      <v-file-input
        ref="fileInput"
        :rules="validation.fileInputRules"
        class="epost__file-selector"
        prepend-icon=""
        v-model="formData.files"
        outlined
        :clearable="false"
        multiple
        @change="filesChanged"
        @click="checkFileLength($event)"
        background-color="white"
        accept=".pdf,.jpg,.jpeg,.gif,.png,.bmp,.tiff,.tif"
        :disabled="loading"
      >
        <template v-slot:selection="{ index }">
          <div class="epost__file-item">
            <a
              class="epost__file-item-link"
              :class="$vuetify.breakpoint.xsOnly ? 'epost__file-item-link--disabled' : ''"
              target="_blank"
              :href="getUrlForFile(formData.files[index])"
              @click.stop
              >{{ formData.files[index].name }}</a
            >

            <v-icon class="epost__remove-item-icon" @click.stop="removeItem(index)"
              >mdi-delete-outline</v-icon
            >
          </div>
        </template>
      </v-file-input>

      <v-alert
        class="epost__file-error"
        transition="fade-transition"
        v-for="(error, index) in fileErrorsOut"
        border="left"
        dense
        text
        outlined
        :key="index"
        type="error"
        v-html="error"
      ></v-alert>
      <h2 class="epost__subtitle">
        <sc-text :field="fields.TermsConditionsSubtitle" />
      </h2>
      <div>
        <sc-rich-text :field="fields.TermsConditionsDescription" />
      </div>
      <v-btn :disabled="!formValid" :loading="loading" type="submit" class="epost__submit-button">
        <sc-text :field="fields.FormSubmitButtonLabel" />
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import {
  Text,
  isExperienceEditorActive,
  Placeholder,
  RichText,
} from '@sitecore-jss/sitecore-jss-vue';
import { validation } from '../../lib/validation';
import { submitDocuments } from '../../api/index';

const emptyField = { value: '' };

export default {
  name: 'EPost',
  data: function () {
    return {
      requestTypeItems: [
        { text: 'New application', key: 'application', value: 'Application' },
        { text: 'Maturity instructions', key: 'maturityInstructions', value: 'Maturity' },
        { text: 'Update my details', key: 'updateDetails', value: 'ChangeOfDetails' },
        { text: 'Update my beneficiary', key: 'updateBeneficiary', value: '_ChangeOfDetails' }, // '_' will be trimmed from submitted form value
        { text: 'Withdrawal', key: 'withdrawal', value: 'Withdrawal' },
        { text: 'Other', key: 'other', value: 'General' },
      ],
      tempFormData: {
        requestType: null,
      },
      formData: {
        requestType: null,
        supportingInformation: null,
        files: [],
      },
      filesAppended: [],
      fileErrors: [],
      fileErrorsOut: [],
      formValid: false,
      inputElement: null,
      errorNotificationTTL: parseInt(this.$t('ePostNotificationTTL')) || 3000,
      validation: {
        requestTypeRules: [
          (v) => validation.valueRequired(v) || this.$t('validation-requestTypeRequired'),
        ],
        fileInputRules: [
          (v) => validation.epostMinFilesSelected(v) || this.$t('validation-fileRequired'),
          (v) =>
            validation.epostMaxFilesSelected(v) || this.$t('validation-collectiveTooManyFiles'),
          (v) =>
            validation.epostIndividualFileSize(v) ||
            this.$t('validation-collectiveFileSizeTooLarge'),
          (v) =>
            validation.epostFileExtension(v) ||
            this.$t('validation-collectiveFileExtensionInvalid'),
        ],
      },
      ePostFeature: this.$envConfig.features.ePost,
      loading: false,
    };
  },
  components: {
    ScText: Text,
    ScRichText: RichText,
    ScPlaceholder: Placeholder,
  },
  computed: {
    isSitecoreExperienceEditorOn() {
      // TODO may need to add other check like the preview mode
      return isExperienceEditorActive();
    },
    requestTypeValue() {
      return this.requestTypeItems.find((i) => i.key === this.formData.requestType).value;
    },
	requestTypeKey() {
      return this.requestTypeItems.find((i) => i.value === this.tempFormData.requestType).key;
    },
  },
  props: {
    fields: {
      type: Object,
      default: () => ({
        ComponentTitle: emptyField,
        ComponentDescription: emptyField,
        RequestTypeLabel: emptyField,
        RequestTypePlaceholder: emptyField,
        SupportingInformationLabel: emptyField,
        SupportingInformationPlaceholder: emptyField,
        SupportingInformationCharacterLimit: emptyField,
        FileSelectionSubtitle: emptyField,
        FileSelectionDescription: emptyField,
        FileSelectionLabel: emptyField,
        FileSelectionPlaceholder: emptyField,
        FileSelectionButtonLabel: emptyField,
        TermsConditionsSubtitle: emptyField,
        TermsConditionsDescription: emptyField,
        FormSubmitButtonLabel: emptyField,
      }),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    'tempFormData.requestType': function (val) {
      this.formData.requestType = val.replace('_', '');
    },
    fileErrors: function (val) {
      if (val.length > 0) {
        setTimeout(() => this.fileErrorsOut.push(val.shift()), 100);
      }
    },
    fileErrorsOut: function (val) {
      if (val.length > 0) {
        setTimeout(() => this.fileErrorsOut.shift(), this.errorNotificationTTL);
      }
    },
  },
  methods: {
    getUrlForFile: function (file) {
      return window.URL.createObjectURL(file);
    },
    removeItem: function (index) {
      this.formData.files.splice(index, 1);
    },
    filesUpdated: function (event) {
      console.log('Files: %o', this.formData.files);
      console.log('Valid: %o', this.$refs.fileInput.valid);
    },
    checkFileLength: function (event) {
      if (this.formData.files.length >= 5) {
        event.preventDefault();
      }
    },
    filesChanged: function (event) {
      // appends newly selected files with existing files - gets distinct files (no duplicates)
      if (this.filesAppended == event) {
        return;
      }

      let appendedFiles = [...this.sortFilesByName(this.filesAppended), ...this.sortFilesByName([...event])];
  
      if (this.ePostFeature.preventDuplicateFiles) {
        appendedFiles = appendedFiles.reduce((accumulator, item) => {
          if (accumulator.some((otherItem) => this.filesEqual(item, otherItem))) {
            return accumulator;
          }
          return [...accumulator, item];
        }, []);
      }

      // this section performs the same validation as the file input :rules, but runs them against each file individually so we can get detailed errors and remove them from the final set of files

        // remove files that are too big
        appendedFiles = this.getSanitisedSet(
          appendedFiles,
          validation.epostIndividualFileSize,
          this.$t('validation-specificFileSizeTooLarge')
        );

        // remove files that have an invalid extension
        appendedFiles = this.getSanitisedSet(
          appendedFiles,
          validation.epostFileExtension,
          this.$t('validation-specificFileExtensionInvalid')
        );

        //remove files in excess of max file count
        if (appendedFiles.length > 5) {
          appendedFiles = appendedFiles.slice(0, 5);
          this.addError(this.$t('validation-collectiveTooManyFiles'));
        }


      appendedFiles = this.sortFilesByName(appendedFiles);

      //update form files model and the copy
      this.filesAppended = this.formData.files = appendedFiles;
    },
    sortFilesByName (files) {
      return files.sort((a, b) => a.name.localeCompare(b.name));
    },
    getSanitisedSet: function (fileArray, validationFunc, validationMessage) {
      let sanitisedFiles = [];
      fileArray.forEach((file) => {
        if (validationFunc([file])) {
          sanitisedFiles.push(file);
        } else {
          this.addError(validationMessage, file.name);
        }
      });
      return sanitisedFiles;
    },
    addError: function (errorMessage, ...args) {
      const finalErrorMessage = args.reduce(
        (errorMessage, arg, idx) => (errorMessage = errorMessage.replace(`{${idx}}`, arg)),
        errorMessage
      );
      this.fileErrors.push(finalErrorMessage);
    },
    openFileSelection: function () {
      const inputElement =
        this.inputElement || this.$refs.fileInput.$el.querySelector('input[type=file]');
      inputElement.click();
    },
    filesEqual: function (file1, file2) {
      //simple file attribute comparison
      return (
        file1.name === file2.name &&
        file1.lastModified === file2.lastModified &&
        file1.size === file2.size &&
        file1.type === file2.type
      );
    },
    processForm: async function () {
      this.loading = true;
      try {
        let response = await submitDocuments({ ...this.formData });
        this.$router.push({
          path: 'epostsuccess',
          query: { reference: response.data.referenceCode },
        });
		this.$store.dispatch('analytics', {
          actionName: 'ePost-form-complete',
          entityChange: this.requestTypeKey,
        });
      } catch (error) {
        this.addError(this.$t('validation-apiError'));
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.epost {
  color: map-deep-get($colors, 'blue-text', 'base');

  &__title {
    margin-bottom: 24px;
  }

  &__title-section {
    margin-bottom: 50px;
  }

  &__subtitle {
    margin-bottom: 12px;
    span {
      font-size: 1.25rem;
    }
  }

  &__request-type {
    box-shadow: none;
  }

  &__file-item {
    width: 100%;
    line-height: 2rem;
    margin: 5px;
  }

  &__file-item-link {
    @media (hover: hover) {
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    &--disabled {
      pointer-events: none;
      cursor: default;
      color: map-deep-get($colors, 'blue-text', 'base');
      text-decoration: none;
    }
  }

  &__remove-item-icon {
    float: right;
  }

  &__submit-button,
  &__choose-files-button {
    width: 150px;
    height: 48px !important;
    @media #{map-get($display-breakpoints, 'xs-only')} {
      width: 100%;
      margin: 16px 0;
    }
  }

  &__choose-files-button {
    float: right;
  }

  &__choose-files-button {
    @extend .btn-secondary;
  }

  &__submit-button {
    @extend .btn-primary;
    float: right;
  }

  &__file-selector-label {
    clear: both;
    display: block;
  }

  &__file-error {
  }

  &__documents-description {
    margin-bottom: 24px;
  }

  &__field-title,
  &__file-selector-label {
    color: map-deep-get($colors, 'blue-title', 'base');
    display: block;
    margin-bottom: 7px;
  }
}
</style>
<style lang="scss">
.epost {
  fieldset {
    border-radius: 3px !important;
  }
  button {
    box-shadow: none;
    border-radius: 0 !important;
  }
  input[type='checkbox'] {
    border: 1px solid;
  }
  .epost__form *.error--text {
    color: map-deep-get($colors, 'danger', 'base') !important;
    caret-color: map-deep-get($colors, 'danger', 'base') !important;
    border-radius: 3px !important;
    margin-left: 0 !important;
  }
  i.error--text {
    font-size: 1.5rem !important;
  }
  .epost__supporting-information {
    .v-input__slot {
      padding: 0 12px 0 !important;
    }
    textarea {
      padding-top: 4px;
    } 
  }
  .epost__request-type .v-input__slot {
    box-shadow: none !important;
  }
}
</style>